import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { imagesApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import type { ImageMeta } from '../types/api';

/**
 * BulkDeleteImagesModal — confirm + destructive action for removing N
 * images at once. Backend cascades to all boxes via FK; S3 blobs are
 * deleted best-effort. D.14 — parent supplies the selected ID set and
 * the full image list so we can preview filenames; on confirm we POST
 * /projects/:id/images/bulk-delete and emit the submitted IDs back so
 * the parent prunes its state.
 */
interface BulkDeleteImagesModalProps {
    open: boolean;
    onClose: () => void;
    isDarkMode?: boolean;
    projectId: string;
    ids: string[];
    /** Full image list — used to look up filenames for the preview. */
    images: ImageMeta[];
    orgId?: string;
    /** Called with the submitted IDs after the server confirms the delete.
     *  The parent prunes them from local state — anything that was missing
     *  server-side was already gone, so it's still safe to drop. */
    onDeleted: (ids: string[]) => void;
}

const PREVIEW_LIMIT = 5;

export default function BulkDeleteImagesModal({
    open,
    onClose,
    isDarkMode = false,
    projectId,
    ids,
    images,
    orgId,
    onDeleted,
}: BulkDeleteImagesModalProps) {
    const { t: tCommon } = useTranslation('common');
    const { t: tCanvas } = useTranslation('canvas');
    const { t: tErrors } = useTranslation('errors');
    const [submitting, setSubmitting] = useState(false);

    const count = ids.length;

    // Look up filenames in the same order as `ids`. Falls back to a short
    // hash if the row isn't in the in-memory list (shouldn't happen but
    // keeps the modal robust).
    const filenames = useMemo(() => {
        const byId = new Map(images.map((img) => [img.id, img.filename]));
        return ids.map((id) => byId.get(id) ?? id.slice(0, 8));
    }, [ids, images]);

    if (count === 0) return null;

    const previewFiles = filenames.slice(0, PREVIEW_LIMIT);
    const overflow = Math.max(0, filenames.length - PREVIEW_LIMIT);

    async function confirmDelete() {
        setSubmitting(true);
        try {
            const res = await imagesApi.bulkRemove(projectId, ids, orgId);
            toast.success(tCanvas('images_bulk_deleted', {
                defaultValue: '{{count}} images deleted',
                count: res.deleted_count,
            }));
            // Server filtered by org+project; the submitted ID set is what
            // the user saw and what we should prune locally.
            onDeleted(ids);
            onClose();
        } catch (err) {
            if (err instanceof APIClientError && err.code === 'project_locked') {
                toast.error(tErrors('project_locked'));
            } else if (err instanceof APIClientError && err.code === 'too_many_ids') {
                toast.error(tErrors('bulk_delete_too_many', {
                    defaultValue: 'Cannot delete more than 200 images at once.',
                }));
            } else {
                toast.error(tErrors('generic'));
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={submitting ? () => {} : onClose}
            title={tCanvas('bulk_delete_images_title', { defaultValue: 'Delete selected images' })}
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose} disabled={submitting}>
                        {tCommon('cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => void confirmDelete()}
                        disabled={submitting}
                    >
                        {submitting
                            ? tCanvas('deleting', { defaultValue: 'Deleting…' })
                            : tCanvas('delete_n_images', {
                                defaultValue: 'Delete {{count}} images',
                                count,
                            })}
                    </Button>
                </>
            }
        >
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                {tCanvas('bulk_delete_images_body', {
                    defaultValue: 'Delete {{count}} images? This cannot be undone.',
                    count,
                })}
            </p>
            <ul
                className={cn(
                    'mt-3 rounded-lg border p-3 text-sm space-y-1 font-mono',
                    isDarkMode ? 'border-gray-700 bg-gray-800/40 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700',
                )}
            >
                {previewFiles.map((name, idx) => (
                    <li key={`${name}-${idx}`} className="truncate">
                        {name}
                    </li>
                ))}
                {overflow > 0 && (
                    <li className={cn('italic', isDarkMode ? 'text-gray-500' : 'text-gray-500')}>
                        … +{overflow} more
                    </li>
                )}
            </ul>
        </Modal>
    );
}
